import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { isBrowser, getCookie } from "@helpers/browser";

const ExponeaContext = createContext({
  isExponeaLoaded: false,
  dataExponea: null,
});

export function useExponea() {
  return useContext(ExponeaContext);
}

const EXPONEA_COOKIE_NAME = "__exponea_etc__"; // Added by Exponea

/*
 * The goal of this hook is to know when Exponea is "fully loaded"
 * Being "fully loaded" means that exponea is either
 * - Not initialized (because blocked by ADBlock or else)
 * - Initialized but without customer data
 * - Initialized with customer data *entirely loaded*
 *
 * The complexity of the current code comes from the impossibility of
 * determining when exponea is still loading customer data from when it has no
 * data on the customer (new customer).
 * We also want to be able to get the result as fast as possible.
 * Especially in the scenarios where the data do not exist/cannot be retrieved.
 *
 * Key points in the scenarios are:
 * ┌──────────────────┐
 * │  Exponea script  ├────┐
 * │     is loaded    │    │
 * └──────────────────┘    │
 *         ▼  yes          │
 * ┌──────────────────┐    │
 * │     Previous     ├────┤
 * │  cookie is found │    │
 * └──────────────────┘    │ no
 *         ▼  yes          │
 * ┌──────────────────┐    │
 * │   Has finished   ├────┤
 * │loading user data │    │
 * └──────────────────┘    │
 *         ▼  yes          │
 * ┌──────────────────┐    │
 * │ LOADING COMPLETE │◄───┘
 * └──────────────────┘
 *
 */
function useExponeaIsLoaded() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasExponea, setHasExponea] = useState(isBrowser && typeof window.exponea !== "undefined");
  const [hasFoundUser, setHasFoundUser] = useState(false);
  const [cookieLoadingTimeout, setCookieLoadingTimeout] = useState(false);

  const checkExponeaDataLoaded = useCallback((attempt = 0) => {
    const key = "identified";
    const timeoutInMS = 5000;

    if (window.exponea?.[key] !== undefined) {
      setIsLoaded(true);
      return;
    }
    const totalWaitedTimeInMS = 50 * attempt;
    if (totalWaitedTimeInMS < timeoutInMS) {
      setTimeout(() => checkExponeaDataLoaded(attempt + 1), 200);
    }
  }, []);

  // Case for when cookie is found but not loaded (extreme side case)
  useEffect(() => {
    if (cookieLoadingTimeout && !hasFoundUser) {
      setIsLoaded(true);
    }
  }, [cookieLoadingTimeout, hasFoundUser]);

  useEffect(() => {
    if (isBrowser && hasExponea) {
      window.exponea.start({
        cookies: {
          retrieve_callback: () => {
            // Once this callback is called, Exponea is in the process to retrieve all data of the customer
            // `checkExponeaDataLoaded()` will run until the data is found or until we reach a timeout of 5s
            setHasFoundUser(true);
            checkExponeaDataLoaded();
          },
        },
      });
      // If no cookie found the above 'retrieve_callback' will not be called so we can directly return
      if (!getCookie(EXPONEA_COOKIE_NAME)) {
        setCookieLoadingTimeout(true);
        return;
      }
      // If a cookie is found 'retrieve_callback' is supposed to be fired but we still make a timeout in case it is not fired
      const timeout = setTimeout(() => {
        setCookieLoadingTimeout(true);
      }, 500);
      // eslint-disable-next-line consistent-return
      return () => clearTimeout(timeout);
    }
    // if Exponea script is not loaded we consider that it finished loading (ex: blocked by ad-block)
    setIsLoaded(true);
  }, [hasExponea, checkExponeaDataLoaded]);

  // Check at every render of the provider if Exponea *script* loaded in case it wasn't
  if (!hasExponea && isBrowser && typeof window.exponea !== "undefined") {
    setHasExponea(true);
  }

  return isLoaded;
}

function buildDataExponea() {
  const exponea = window.exponea || {};

  return {
    segmentP: exponea.segmentP || 0,
    consent_email: exponea.consent_email === "true",
    consent_sms: exponea.consent_sms === "true",
    identified: exponea.identified === "true",
    lastViewed: Array.isArray(exponea.lastViewed) ? exponea.lastViewed : [],
    phone: exponea.phone !== "None" ? exponea.phone : null,
    wishlistItems: Array.isArray(exponea.wishlistItems) ? exponea.wishlistItems : [],
    email_id:
      exponea.email_id && exponea.email_id !== "null" && exponea.email_id !== "false"
        ? exponea.email_id
        : null,
  };
}

export default function ExponeaProvider({ children }) {
  const [state, setState] = useState({ isExponeaLoaded: false, dataExponea: {} });
  const isExponeaLoaded = useExponeaIsLoaded();

  useEffect(() => {
    if (!isExponeaLoaded) {
      return undefined;
    }

    if (!isBrowser) {
      setState({ isExponeaLoaded: true });
      return undefined;
    }

    const syncExponeaData = () => {
      setState({ isExponeaLoaded: true, dataExponea: buildDataExponea() });
    };

    syncExponeaData();

    // lastViewed is set after identified; re-sync once it exists (see checkExponeaDataLoaded)
    if (window.exponea?.lastViewed !== undefined) {
      return undefined;
    }

    let attempt = 0;
    const intervalId = setInterval(() => {
      if (window.exponea?.lastViewed !== undefined) {
        syncExponeaData();
        clearInterval(intervalId);
        return;
      }

      attempt += 1;
      if (attempt >= 25) {
        clearInterval(intervalId);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [isExponeaLoaded]);

  // `window.exponea.identified` can be set before `lastViewed` is populated,
  // causing isExponeaLoaded to fire while lastViewed is still empty.
  useEffect(() => {
    if (!isExponeaLoaded || !isBrowser) return;
    if (window.exponea?.lastViewed?.length) return;

    let attempts = 0;
    const maxAttempts = 15; // 200ms × 15 = 3 seconds
    const intervalId = setInterval(() => {
      attempts += 1;
      if (window.exponea?.lastViewed?.length) {
        clearInterval(intervalId);
        setState((prev) => ({
          ...prev,
          dataExponea: {
            ...prev.dataExponea,
            lastViewed: window.exponea.lastViewed,
          },
        }));
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [isExponeaLoaded]);

  return <ExponeaContext.Provider value={state}>{children}</ExponeaContext.Provider>;
}
